import {
  CommonModule,
  CurrencyPipe,
  DatePipe,
  NgClass,
  registerLocaleData,
} from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import localeEsCO from '@angular/common/locales/es-CO';

registerLocaleData(localeEsCO);
import { ActivatedRoute, Router } from '@angular/router';
import { jsPDF } from 'jspdf';
import {
  ApiErrorService,
  BusinessMembershipDto,
  EpaycoLoaderService,
  GetLatestBusinessMembershipResponse,
  GetMembershipPlansResponse,
  IpService,
  MembershipPlanBasicDto,
  MembershipPlanDto,
  MembershipPlanFeatureDto,
  MembershipService as MembershipApiService,
  PaymentHistoryItemDto,
  PageTitleService,
} from '../../../shared/services';
import { forkJoin, firstValueFrom, map } from 'rxjs';
import { BaseAuthComponent } from '../../../shared/common/BaseAuthComponent.class';
import { addMonthsSafe, addYearsSafe } from './billingDate';
import { PaymentsService } from '../../../services';
import { MembershipService as MembershipStatusService, trialToneFor } from '../../../services/membership/membership.service';
import { environment } from '../../../../environments/environment';
import {
  PaymentOptionsModal,
  PaymentOption,
  PriceBreakdown,
  PriceBreakdownItem,
} from './payment-options-modal/payment-options-modal';

type BillingCycle = 'monthly' | 'yearly';
type InvoiceStatus = 'paid' | 'pending' | 'failed' | 'refunded';

// ePayco redirects back before its confirmation webhook is processed; poll until it lands

interface Plan {
  code: 'starter' | 'pro' | 'business';
  name: string;
  priceMonthly: number;
  priceYearly: number;
  features: string[];
  trialDays?: number;
}
interface PaymentMethod {
  brand: 'visa' | 'mastercard' | 'amex' | 'diners' | 'discover';
  last4: string;
  expMonth: number;
  expYear: number;
}
interface InvoiceFeature {
  featureName: string;
  pricePaid: number;
  quantity: number;
}

interface Invoice {
  id: string;
  createdAt: string;
  amount: number;
  currency?: string;
  status: InvoiceStatus;
  method?: PaymentMethod;
  receiptUrl?: string;
  description?: string;
  period?: { start: string; end: string };
  failureReason?: string | null;
  externalPaymentId?: string | null;
  externalTransactionId?: string | null;
  planName?: string | null;
  features?: InvoiceFeature[];
}

interface PaymentCallbackResult {
  refPayco?: string;
  transactionId?: string;
  status?: string;
  message?: string;
  amount?: number;
  currency?: string;
  isTest?: boolean;
}

type PaymentResultVariant = 'success' | 'warning' | 'error' | 'info';

interface PaymentResultStyle {
  container: string;
  accent: string;
  iconBg: string;
}

const PAYMENT_RESULT_STYLES: Record<PaymentResultVariant, PaymentResultStyle> =
  {
    success: {
      container:
        'border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200',
      accent: 'text-emerald-700 dark:text-emerald-300',
      iconBg:
        'bg-emerald-100 text-emerald-600 dark:bg-emerald-600/30 dark:text-emerald-200',
    },
    warning: {
      container:
        'border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200',
      accent: 'text-amber-700 dark:text-amber-300',
      iconBg:
        'bg-amber-100 text-amber-700 dark:bg-amber-600/30 dark:text-amber-200',
    },
    error: {
      container:
        'border border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200',
      accent: 'text-rose-700 dark:text-rose-300',
      iconBg:
        'bg-rose-100 text-rose-700 dark:bg-rose-600/30 dark:text-rose-200',
    },
    info: {
      container:
        'border border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-200',
      accent: 'text-sky-700 dark:text-sky-300',
      iconBg: 'bg-sky-100 text-sky-700 dark:bg-sky-600/30 dark:text-sky-200',
    },
  };

@Component({
  selector: 'app-membership',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DatePipe, NgClass, PaymentOptionsModal],
  templateUrl: './membership.html',
  styleUrl: './membership.css',
})
export class Membership extends BaseAuthComponent implements OnInit {
  private readonly titleService = inject(PageTitleService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly membershipSvc = inject(MembershipApiService);
  private readonly membershipStatusSvc = inject(MembershipStatusService);
  private readonly apiErrorService = inject(ApiErrorService);
  isLoadingData = signal<boolean>(false);
  latestMembership = signal<BusinessMembershipDto | null>(null);
  /** With a renewal paid in advance, the next charge is the day after the prepaid period ends */
  prepaidNextCharge = computed<Date | null>(() => {
    const paidThrough = this.latestMembership()?.paidThroughDate;
    if (!paidThrough) return null;
    const next = new Date(paidThrough);
    next.setUTCDate(next.getUTCDate() + 1);
    return next;
  });
  payment = inject(PaymentsService);
  epaycoLoader = inject(EpaycoLoaderService);
  ipService = inject(IpService);
  clientIp: string = '';

  billingCycle = signal<BillingCycle>('monthly');

  plan = signal<Plan>({
    code: 'pro',
    name: 'Profesional',
    priceMonthly: 99000,
    priceYearly: 1009800,
    features: [
      'Agendas ilimitadas',
      'Recordatorios por WhatsApp',
      'Múltiples profesionales',
      'Reportes y exportación',
    ],
  });

  // Multi-plan support
  allPlans = signal<MembershipPlanDto[]>([]);
  selectedPlanId = signal<string | null>(null);

  // New signals for additional features and professionals
  selectedFeatures = signal<Map<string, boolean>>(new Map());
  numberOfProfessionals = signal<number>(0); // 0 = solo el profesional incluido, 1+ = adicionales
  hasUserModifiedSelections = signal<boolean>(false);
  currentPlan = signal<MembershipPlanDto | null>(null);

  invoices = signal<Invoice[]>([]);
  isPaymentHistoryLoading = signal(false);
  paymentHistoryError = signal<string | null>(null);

  /** Payments shown before "Ver todos" */
  private readonly recentPaymentsLimit = 6;
  showAllPayments = signal(false);
  visiblePayments = computed(() =>
    this.showAllPayments() ? this.invoices() : this.invoices().slice(0, this.recentPaymentsLimit),
  );
  hiddenPaymentsCount = computed(() => Math.max(0, this.invoices().length - this.recentPaymentsLimit));

  /** Professional seats the business can use, same rule as Configuración and Profesionales */
  seatCount = signal<number | null>(null);

  /** True once the latest membership request has finished, ok or not */
  membershipLoaded = signal(false);

  /** The latest membership's state, normalized for the current-plan card */
  membershipState = computed<'active' | 'trial' | 'expired' | 'suspended' | 'cancelled' | 'pending' | 'none'>(() => {
    const lm = this.latestMembership();
    if (!lm) return 'none';
    const status = lm.membershipStatus?.name?.toLowerCase() ?? '';
    if (status === 'active' || status === 'trial' || status === 'suspended' || status === 'cancelled' || status === 'pending') {
      return status;
    }
    return 'expired';
  });

  /** Active or trial membership: the card offers to change the plan rather than renew it */
  hasCurrentPlan = computed(() => ['active', 'trial'].includes(this.membershipState()));

  isTrial = computed(() => this.membershipState() === 'trial');

  /** Days elapsed and left in the free trial, for the trial progress bar */
  trialProgress = computed(() => {
    const lm = this.latestMembership();
    if (!this.isTrial() || !lm?.trialStartDate || !lm.trialEndDate) return null;
    const day = 86_400_000;
    const start = new Date(lm.trialStartDate).getTime();
    const end = new Date(lm.trialEndDate).getTime();
    const total = Math.max(1, Math.round((end - start) / day));
    // The backend counts in the business timezone; the sidebar shows that same number
    const left = this.membershipStatusSvc.trialDaysLeft() ?? Math.max(0, Math.ceil((end - Date.now()) / day));
    return { total, left, percent: Math.min(100, Math.round(((total - left) / total) * 100)) };
  });

  /** Trial notice color, the same scale the sidebar uses */
  trialTone = computed(() => trialToneFor(this.trialProgress()?.left ?? null));

  /**
   * The next plan up from the one being looked at (the current plan on the overview, the
   * selected one while choosing), with what it adds, so the page can suggest the upgrade.
   */
  upgradeOffer = computed(() => {
    const refId =
      this.view() === 'plans' ? this.selectedPlanId() : this.latestMembership()?.membershipPlanId;
    const plans = this.sortedPlans();
    const index = plans.findIndex((p) => p.membershipPlanId === refId);
    const next = index >= 0 ? plans[index + 1] : undefined;
    if (!next) return null;
    const ref = plans[index];
    const gained = this.planFeatureDelta(next).features.map((f) => f.featureName);
    const extraPerMonth = Math.max(0, next.priceMonthly - ref.priceMonthly);
    const extraProfessionals = Math.max(0, (next.numberOfProfessionals || 1) - (ref.numberOfProfessionals || 1));
    // What the same gains cost as add-ons on the lower plan, to show what the upgrade saves
    const asAddOns =
      extraProfessionals * (ref.pricePerProfessional ?? 0) +
      this.getPlanAdditionalFeatures(ref)
        .filter((f) => gained.includes(f.featureName))
        .reduce((sum, f) => sum + (f.priceMonthly ?? 0), 0);
    return {
      plan: next,
      extraPerMonth,
      extraProfessionals,
      features: gained,
      savings: Math.max(0, asAddOns - extraPerMonth),
    };
  });

  /** "2 profesionales más, Inventario, Reportes y 1 función más", for the upgrade suggestion */
  upgradeGains = computed(() => {
    const offer = this.upgradeOffer();
    if (!offer) return '';
    const parts = [
      ...(offer.extraProfessionals
        ? [`${offer.extraProfessionals} profesional${offer.extraProfessionals > 1 ? 'es' : ''} más`]
        : []),
      ...offer.features.slice(0, 2),
      ...(offer.features.length > 2
        ? [`${offer.features.length - 2} función${offer.features.length > 3 ? 'es' : ''} más`]
        : []),
    ];
    return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}` : (parts[0] ?? '');
  });

  /** Opens the plan picker with the suggested plan already selected */
  chooseUpgrade() {
    const offer = this.upgradeOffer();
    if (!offer) return;
    this.selectPlan(offer.plan);
    if (this.view() !== 'plans') this.openPlans();
  }

  /** What the plan costs per billing period, add-ons included */
  currentPeriodTotal = computed(() => {
    const lm = this.latestMembership();
    if (!lm) return 0;
    const yearly = /annual|year/i.test(lm.billingCycle?.name ?? '');
    const base = yearly ? lm.membershipPlan.priceAnnual : lm.membershipPlan.priceMonthly;
    return base + this.currentAddOns().reduce((sum, f) => sum + (f.pricePaid || 0), 0);
  });

  isYearlyMembership = computed(() => /annual|year/i.test(this.latestMembership()?.billingCycle?.name ?? ''));

  /** Add-ons paid for the period that is running now */
  currentAddOns = computed(() => {
    const today = new Date().toISOString().slice(0, 10);
    return (this.latestMembership()?.additionalFeatures ?? []).filter(
      (f) => !f.billingPeriodEnd || f.billingPeriodEnd.slice(0, 10) >= today,
    );
  });

  private viewParam = toSignal(
    this.route.queryParamMap.pipe(map((p) => p.get('view'))),
    { initialValue: null },
  );

  /**
   * "overview" shows the current plan and payments; "plans" is the change-plan step.
   * Without any membership there is nothing to summarize, so the picker opens directly;
   * an expired one keeps its summary so it can be renewed in one step.
   */
  view = computed<'overview' | 'plans'>(() => {
    if (this.viewParam() === 'plans') return 'plans';
    return this.membershipLoaded() && this.membershipState() === 'none' ? 'plans' : 'overview';
  });

  openPlans() {
    this.router.navigate([], { relativeTo: this.route, queryParams: { view: 'plans' } });
    window.scrollTo({ top: 0 });
  }

  closePlans() {
    this.router.navigate([], { relativeTo: this.route, queryParams: {} });
    window.scrollTo({ top: 0 });
  }

  private readonly destroyRef = inject(DestroyRef);
  isDownloadingMembershipReport = signal(false);
  paymentResult = signal<PaymentCallbackResult | null>(null);
  paymentResultVariant = computed<PaymentResultVariant>(() =>
    this.resolvePaymentVariant(this.paymentResult()),
  );
  paymentResultStyles = computed<PaymentResultStyle>(
    () => PAYMENT_RESULT_STYLES[this.paymentResultVariant()],
  );
  paymentResultTitle = computed(() =>
    this.resolvePaymentTitle(this.paymentResultVariant()),
  );

  nextChargeDate = computed(() => {
    const today = new Date();
    const lm = this.latestMembership();

    // Si hay una membresía actual, calcular basándose en sus fechas
    if (lm) {
      // Prioridad 1: Usar nextBillingDate si existe y está en el futuro
      // nextBillingDate es la fecha real del próximo cobro
      if (lm.nextBillingDate) {
        const nextBilling = new Date(lm.nextBillingDate);
        if (nextBilling > today) {
          // Si el ciclo seleccionado es el mismo, usar nextBillingDate directamente
          const currentCycle = (lm.billingCycle?.name || '').toLowerCase();
          const selectedCycle = this.billingCycle();
          const isSameCycle =
            (currentCycle.includes('month') && selectedCycle === 'monthly') ||
            (currentCycle.includes('year') && selectedCycle === 'yearly');

          if (isSameCycle) {
            return nextBilling;
          }

          // Si cambió el ciclo, calcular desde endDate si existe, sino desde nextBillingDate
          const baseDate =
            lm.endDate && new Date(lm.endDate) > today
              ? new Date(lm.endDate)
              : nextBilling;
          return selectedCycle === 'yearly'
            ? addYearsSafe(baseDate, 1)
            : addMonthsSafe(baseDate, 1);
        }
      }

      // Prioridad 2: Si hay endDate en el futuro pero no nextBillingDate,
      // el próximo cobro es el día DESPUÉS de endDate (endDate es el último día inclusivo del período)
      if (lm.endDate) {
        const endDate = new Date(lm.endDate);
        if (endDate > today) {
          const nextDay = new Date(endDate);
          nextDay.setDate(nextDay.getDate() + 1);
          return nextDay;
        }
      }

      // Prioridad 3: Si hay trial activo, usar trialEndDate
      if (lm.trialEndDate) {
        const trialEnd = new Date(lm.trialEndDate);
        if (trialEnd > today) {
          // Después del trial, el siguiente cobro sería trialEnd + ciclo seleccionado
          return this.billingCycle() === 'yearly'
            ? addYearsSafe(trialEnd, 1)
            : addMonthsSafe(trialEnd, 1);
        }
      }
    }

    // Si no hay membresía o está vencida, calcular desde hoy
    const td = this.plan().trialDays ?? 0;

    // Si hay trial, el siguiente cobro es al finalizar el trial + ciclo seleccionado
    if (td > 0) {
      const trialEnd = new Date(today);
      trialEnd.setDate(trialEnd.getDate() + td);
      // Después del trial, se cobraría según el ciclo seleccionado
      return this.billingCycle() === 'yearly'
        ? addYearsSafe(trialEnd, 1)
        : addMonthsSafe(trialEnd, 1);
    }

    // Sin trial: hoy + ciclo seleccionado
    return this.billingCycle() === 'yearly'
      ? addYearsSafe(today, 1)
      : addMonthsSafe(today, 1);
  });

  currentMembershipEndDate = computed(() => {
    const lm = this.latestMembership();
    if (lm?.endDate) {
      return new Date(lm.endDate);
    }
    return null;
  });

  showPay = signal(false);
  isProcessingPayment = signal(false);
  paymentError = signal<string | null>(null);
  showPaymentOptionsModal = signal(false);
  selectedPaymentOption = signal<PaymentOption | null>(null);
  isPaymentHistoryExpanded = signal(false);

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Membresía',
      subtitle: 'Tu plan actual y tus pagos',
      breadcrumbs: [{ name: 'Configuración', url: '/settings' }],
    });

    if (!this.businessId) {
      this.loadPlansOnly();
      this.membershipLoaded.set(true);
    } else {
      this.loadPlansAndLatestMembership(this.businessId);
    }
    this.ipService.getPublicIp().subscribe((ip) => {
      this.clientIp = ip;
    });
    this.loadPaymentHistory();
    this.membershipStatusSvc.membershipUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadAfterPayment());
    this.handlePaymentCallback();
  }

  // Computed signals for features
  includedFeatures = computed(
    () => this.currentPlan()?.features.filter((f) => f.isIncluded) ?? [],
  );

  additionalFeatures = computed(
    () =>
      this.currentPlan()
        ?.features.filter((f) => !f.isIncluded)
        .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999)) ??
      [],
  );

  // Price calculations
  basePlanPrice = computed(() => {
    const monthly =
      this.currentPlan()?.priceMonthly ?? this.plan().priceMonthly;
    if (this.billingCycle() === 'monthly') {
      return monthly;
    }
    // Annual: (monthly * 12) * 0.85
    return monthly * 12 * 0.85;
  });

  // Calculate days remaining in current membership period (for display purposes)
  daysRemainingInPeriod = computed(() => {
    const latestMembership = this.latestMembership();
    if (!latestMembership?.endDate) {
      return 0; // No active membership
    }

    const today = new Date();
    const endDate = new Date(latestMembership.endDate);

    // Check if membership is active and not expired
    if (endDate <= today) {
      return 0; // Membership expired
    }

    // Check if membership status is active
    const status = latestMembership.membershipStatus?.name?.toLowerCase();
    if (status !== 'active') {
      return 0; // Not an active membership
    }

    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  });

  additionalFeaturesPrice = computed(() => {
    let total = 0;
    const isAnnual = this.billingCycle() === 'yearly';

    this.selectedFeatures().forEach((selected, featureId) => {
      if (selected) {
        const feature = this.additionalFeatures().find(
          (f) => f.membershipPlanFeatureId === featureId,
        );
        if (feature) {
          // Exclude "Múltiples profesionales" from this calculation - its cost is calculated separately
          if (feature.featureName === 'Múltiples profesionales') {
            return; // Skip this feature, it's handled in professionalsCost
          }

          const monthlyPrice = feature.priceMonthly ?? 0;
          let featurePrice = 0;

          // Always calculate full period price (no proration)
          if (isAnnual) {
            featurePrice = monthlyPrice * 12 * 0.85;
          } else {
            featurePrice = monthlyPrice;
          }

          total += featurePrice;
        }
      }
    });
    return total;
  });

  professionalsCost = computed(() => {
    const multiplesProfesionales = this.additionalFeatures().find(
      (f) => f.featureName === 'Múltiples profesionales',
    );
    if (
      multiplesProfesionales &&
      this.isFeatureSelected(multiplesProfesionales.membershipPlanFeatureId)
    ) {
      const additionalProfessionals = this.numberOfProfessionals(); // Ya representa solo adicionales
      const currentPlan = this.currentPlan();
      const pricePerProfessional = currentPlan?.pricePerProfessional ?? null;

      // Debug: Log para verificar valores
      if (pricePerProfessional === null || pricePerProfessional === undefined) {
        console.warn('pricePerProfessional is not available in currentPlan:', {
          currentPlan,
          pricePerProfessional,
          planId: currentPlan?.membershipPlanId,
        });
        return 0;
      }

      // If pricePerProfessional is 0 or not set, return 0
      if (pricePerProfessional <= 0) {
        console.warn(
          'pricePerProfessional is 0 or negative:',
          pricePerProfessional,
        );
        return 0;
      }

      // numberOfProfessionals already represents additional professionals (0 = only included, 1+ = additional)
      if (additionalProfessionals <= 0) return 0;

      const isAnnual = this.billingCycle() === 'yearly';
      let cost = 0;

      // Always calculate full period price (no proration)
      if (isAnnual) {
        // Annual: (monthly * 12) * 0.85
        cost = pricePerProfessional * additionalProfessionals * 12 * 0.85;
      } else {
        // Monthly: monthly price
        cost = pricePerProfessional * additionalProfessionals;
      }

      console.log('Professionals cost calculation:', {
        additionalProfessionals,
        pricePerProfessional,
        billingCycle: this.billingCycle(),
        isAnnual,
        cost,
        calculation: isAnnual
          ? `(${pricePerProfessional} * ${additionalProfessionals} * 12) * 0.85 = ${cost}`
          : `${pricePerProfessional} * ${additionalProfessionals} = ${cost}`,
      });

      return cost;
    }
    return 0;
  });

  // Calculate monthly subtotal (before annual discount)
  monthlySubtotal = computed(() => {
    const baseMonthly =
      this.currentPlan()?.priceMonthly ?? this.plan().priceMonthly;
    let featuresMonthly = 0;
    this.selectedFeatures().forEach((selected, featureId) => {
      if (selected) {
        const feature = this.additionalFeatures().find(
          (f) => f.membershipPlanFeatureId === featureId,
        );
        if (feature) {
          featuresMonthly += feature.priceMonthly ?? 0;
        }
      }
    });
    let professionalsMonthly = 0;
    const multiplesProfesionales = this.additionalFeatures().find(
      (f) => f.featureName === 'Múltiples profesionales',
    );
    if (
      multiplesProfesionales &&
      this.isFeatureSelected(multiplesProfesionales.membershipPlanFeatureId)
    ) {
      const additionalProfessionals = this.numberOfProfessionals(); // Ya representa solo adicionales
      const pricePerProfessional =
        this.currentPlan()?.pricePerProfessional ?? 0;
      if (additionalProfessionals > 0) {
        professionalsMonthly = pricePerProfessional * additionalProfessionals;
      }
    }
    return baseMonthly + featuresMonthly + professionalsMonthly;
  });

  // Subtotal sin descuento anual (para mostrar el descuento por separado)
  subtotalBeforeDiscount = computed(() => {
    if (this.billingCycle() === 'yearly') {
      // Para anual, calcular el subtotal mensual * 12 (sin descuento)
      return this.monthlySubtotal() * 12;
    }
    // Para mensual, el subtotal es directo
    return (
      this.basePlanPrice() +
      this.additionalFeaturesPrice() +
      this.professionalsCost()
    );
  });

  subtotal = computed(
    () =>
      this.basePlanPrice() +
      this.additionalFeaturesPrice() +
      this.professionalsCost(),
  );

  annualDiscount = computed(() => {
    if (this.billingCycle() === 'yearly') {
      // Discount = (monthly subtotal * 12) * 0.15
      return this.monthlySubtotal() * 12 * 0.15;
    }
    return 0;
  });

  // Precio a mostrar en la tarjeta (próximo cobro): siempre calcular con el subtotal
  // Esto representa el monto futuro a cobrar, no lo ya pagado en el período.
  displayPrice = computed(() => this.subtotal());

  // Breakdown of selected features for the order summary (individual line items)
  selectedFeaturesBreakdown = computed(() => {
    const items: Array<{ featureId: string; label: string; amount: number }> = [];
    const isAnnual = this.billingCycle() === 'yearly';

    this.selectedFeatures().forEach((selected, featureId) => {
      if (!selected) return;
      const feature = this.additionalFeatures().find(
        (f) => f.membershipPlanFeatureId === featureId,
      );
      if (!feature) return;

      if (feature.featureName === 'Múltiples profesionales') {
        // Show professionals cost with quantity
        const additionalProfessionals = this.numberOfProfessionals();
        if (additionalProfessionals > 0) {
          items.push({
            featureId,
            label: `Profesionales (x${additionalProfessionals})`,
            amount: this.professionalsCost(),
          });
        }
      } else {
        const monthlyPrice = feature.priceMonthly ?? 0;
        if (monthlyPrice > 0) {
          const price = isAnnual ? monthlyPrice * 12 * 0.85 : monthlyPrice;
          items.push({
            featureId,
            label: feature.featureName,
            amount: price,
          });
        }
      }
    });

    return items;
  });

  // Detect if there are new additional features selected that are not in current membership
  hasNewAdditionalFeatures = computed(() => {
    const latestMembership = this.latestMembership();
    const selectedFeatureIds = Array.from(
      this.selectedFeatures().keys(),
    ).filter((id) => {
      // Only consider features that are actually selected (value is true)
      return this.selectedFeatures().get(id) === true;
    });

    // If no features are selected, return false
    if (selectedFeatureIds.length === 0) return false;

    // If there's no membership, any selected feature is "new"
    if (!latestMembership) {
      return selectedFeatureIds.length > 0;
    }

    // Get current feature IDs from membership (convert to strings for comparison)
    const currentFeatureIds =
      latestMembership.additionalFeatures?.map((f) =>
        String(f.membershipPlanFeatureId),
      ) ?? [];

    // Check if there are selected features that are not in the current membership
    // Convert selected IDs to strings for comparison
    // "Múltiples profesionales" is judged by its count below: it can be selected without being a stored
    // feature when the professionals were bought from the Professionals page
    const multiProId = this.additionalFeatures().find(
      (f) => f.featureName === 'Múltiples profesionales',
    )?.membershipPlanFeatureId;
    const hasNewFeature = selectedFeatureIds.some((id) => {
      const idStr = String(id);
      return idStr !== String(multiProId) && !currentFeatureIds.includes(idStr);
    });

    // Also check if the number of additional professionals has increased
    // This handles the case where "Múltiples profesionales" is already selected but user increases the count
    const currentNumberOfProfessionals =
      latestMembership?.numberOfProfessionals ?? 0;
    const currentAdditionalProfessionals =
      this.additionalProfessionalsOf(currentNumberOfProfessionals);
    const newAdditionalProfessionals = this.numberOfProfessionals(); // Already represents only additional professionals
    const hasMoreProfessionals =
      newAdditionalProfessionals > currentAdditionalProfessionals;

    return hasNewFeature || hasMoreProfessionals;
  });

  // Get only the new features (not in current membership)
  getSelectedNewFeatures(): MembershipPlanFeatureDto[] {
    const latestMembership = this.latestMembership();
    const currentFeatureIds =
      latestMembership?.additionalFeatures?.map(
        (f) => f.membershipPlanFeatureId,
      ) ?? [];
    const selectedFeatureIds = Array.from(this.selectedFeatures().keys());

    const newFeatureIds = selectedFeatureIds.filter(
      (id) => !currentFeatureIds.includes(id),
    );
    return this.additionalFeatures().filter((f) =>
      newFeatureIds.includes(f.membershipPlanFeatureId),
    );
  }

  // Calculate full period price for new features in current period only
  currentMonthAdditionalPrice = computed(() => {
    const daysRemaining = this.daysRemainingInPeriod();
    if (daysRemaining <= 0) return 0;

    const newFeatures = this.getSelectedNewFeatures();
    if (newFeatures.length === 0) return 0;

    const isAnnual = this.billingCycle() === 'yearly';
    let total = 0;

    newFeatures.forEach((feature) => {
      // Skip "Múltiples profesionales" - it's handled separately
      if (feature.featureName === 'Múltiples profesionales') {
        return;
      }

      const monthlyPrice = feature.priceMonthly ?? 0;
      if (monthlyPrice > 0) {
        const fullPrice =
          this.membershipStatusSvc.calculateProportionalFeaturePrice(
            daysRemaining,
            monthlyPrice,
            isAnnual,
          );
        total += fullPrice;
      }
    });

    // Handle additional professionals if "Múltiples profesionales" is selected
    // Check if "Múltiples profesionales" is selected (either new or existing)
    const multiplesProfesionalesFeature = this.additionalFeatures().find(
      (f) =>
        f.featureName === 'Múltiples profesionales' &&
        this.isFeatureSelected(f.membershipPlanFeatureId),
    );

    if (multiplesProfesionalesFeature) {
      const latestMembership = this.latestMembership();
      const currentNumberOfProfessionals =
        latestMembership?.numberOfProfessionals ?? 0;
      const currentAdditionalProfessionals =
        this.additionalProfessionalsOf(currentNumberOfProfessionals);
      const newAdditionalProfessionals = this.numberOfProfessionals(); // Already represents only additional professionals

      // Only calculate price for the NEW additional professionals (the increase)
      const professionalsToAdd = Math.max(
        0,
        newAdditionalProfessionals - currentAdditionalProfessionals,
      );
      const pricePerProfessional =
        this.currentPlan()?.pricePerProfessional ?? 0;

      if (professionalsToAdd > 0 && pricePerProfessional > 0) {
        if (isAnnual) {
          const annualPricePerProfessional = pricePerProfessional * 12 * 0.85;
          total += annualPricePerProfessional * professionalsToAdd;
        } else {
          const fullPricePerProfessional =
            this.membershipStatusSvc.calculateProportionalProfessionalPrice(
              daysRemaining,
              pricePerProfessional,
            );
          total += fullPricePerProfessional * professionalsToAdd;
        }
      }
    }

    return total;
  });

  // Calculate price for current month + next membership period
  currentMonthPlusNextPrice = computed(() => {
    const currentMonthPrice = this.currentMonthAdditionalPrice();
    const isAnnual = this.billingCycle() === 'yearly';
    let nextPeriodPrice = 0;

    // 1. Add base plan price
    const basePrice = this.basePlanPrice();
    nextPeriodPrice += basePrice;

    // 2. Add existing features from current membership
    const latestMembership = this.latestMembership();
    if (latestMembership?.additionalFeatures) {
      latestMembership.additionalFeatures.forEach((existingFeature) => {
        // Find the feature definition to get its price
        const featureDef = this.additionalFeatures().find(
          (f) =>
            f.membershipPlanFeatureId ===
            existingFeature.membershipPlanFeatureId,
        );

        if (featureDef) {
          // Skip "Múltiples profesionales" - it's handled separately with professionals
          if (featureDef.featureName === 'Múltiples profesionales') {
            return;
          }

          const monthlyPrice = featureDef.priceMonthly ?? 0;
          if (monthlyPrice > 0) {
            if (isAnnual) {
              nextPeriodPrice += monthlyPrice * 12 * 0.85;
            } else {
              nextPeriodPrice += monthlyPrice;
            }
          }
        }
      });
    }

    // 3. Add existing professionals (from current membership)
    const currentTotalProfessionals =
      latestMembership?.numberOfProfessionals ?? 0;
    if (this.additionalProfessionalsOf(currentTotalProfessionals) > 0) {
      const existingAdditionalProfessionals = this.additionalProfessionalsOf(currentTotalProfessionals);
      const pricePerProfessional =
        this.currentPlan()?.pricePerProfessional ?? 0;

      if (existingAdditionalProfessionals > 0 && pricePerProfessional > 0) {
        if (isAnnual) {
          nextPeriodPrice +=
            pricePerProfessional * existingAdditionalProfessionals * 12 * 0.85;
        } else {
          nextPeriodPrice +=
            pricePerProfessional * existingAdditionalProfessionals;
        }
      }
    }

    // 4. Add new features (only the new ones being added)
    const newFeatures = this.getSelectedNewFeatures();
    newFeatures.forEach((feature) => {
      // Skip "Múltiples profesionales" - it's handled separately
      if (feature.featureName === 'Múltiples profesionales') {
        return;
      }

      const monthlyPrice = feature.priceMonthly ?? 0;
      if (monthlyPrice > 0) {
        if (isAnnual) {
          nextPeriodPrice += monthlyPrice * 12 * 0.85;
        } else {
          nextPeriodPrice += monthlyPrice;
        }
      }
    });

    // 5. Handle new additional professionals (only the increase)
    // Reuse currentTotalProfessionals already declared above
    const currentAdditionalProfessionals =
      this.additionalProfessionalsOf(currentTotalProfessionals);
    const newAdditionalProfessionals = this.numberOfProfessionals(); // Already represents only additional professionals

    // Only calculate price for the NEW additional professionals (the increase)
    const professionalsToAdd = Math.max(
      0,
      newAdditionalProfessionals - currentAdditionalProfessionals,
    );
    const pricePerProfessional = this.currentPlan()?.pricePerProfessional ?? 0;

    if (professionalsToAdd > 0 && pricePerProfessional > 0) {
      if (isAnnual) {
        nextPeriodPrice +=
          pricePerProfessional * professionalsToAdd * 12 * 0.85;
      } else {
        nextPeriodPrice += pricePerProfessional * professionalsToAdd;
      }
    }

    return currentMonthPrice + nextPeriodPrice;
  });

  // Get breakdown for current period full price
  getCurrentMonthBreakdown(): PriceBreakdown {
    const daysRemaining = this.daysRemainingInPeriod();
    const newFeatures = this.getSelectedNewFeatures();
    const isAnnual = this.billingCycle() === 'yearly';
    const items: PriceBreakdownItem[] = [];

    newFeatures.forEach((feature) => {
      if (feature.featureName === 'Múltiples profesionales') {
        return; // Handled separately
      }

      const monthlyPrice = feature.priceMonthly ?? 0;
      const fullPrice =
        this.membershipStatusSvc.calculateProportionalFeaturePrice(
          daysRemaining,
          monthlyPrice,
          isAnnual,
        );
      if (fullPrice > 0) {
        items.push({
          label: feature.featureName,
          amount: fullPrice,
        });
      }
    });

    // Handle additional professionals (only the increase)
    const latestMembership = this.latestMembership();
    const multiplesProfesionalesFeature = this.additionalFeatures().find(
      (f) =>
        f.featureName === 'Múltiples profesionales' &&
        this.isFeatureSelected(f.membershipPlanFeatureId),
    );

    if (multiplesProfesionalesFeature) {
      const currentNumberOfProfessionals =
        latestMembership?.numberOfProfessionals ?? 0;
      const currentAdditionalProfessionals =
        this.additionalProfessionalsOf(currentNumberOfProfessionals);
      const newAdditionalProfessionals = this.numberOfProfessionals(); // Already represents only additional professionals

      // Only calculate price for the NEW additional professionals (the increase)
      const professionalsToAdd = Math.max(
        0,
        newAdditionalProfessionals - currentAdditionalProfessionals,
      );
      const pricePerProfessional =
        this.currentPlan()?.pricePerProfessional ?? 0;

      if (professionalsToAdd > 0 && pricePerProfessional > 0) {
        let professionalsPrice = 0;
        if (isAnnual) {
          const annualPricePerProfessional = pricePerProfessional * 12 * 0.85;
          professionalsPrice = annualPricePerProfessional * professionalsToAdd;
        } else {
          const fullPricePerProfessional =
            this.membershipStatusSvc.calculateProportionalProfessionalPrice(
              daysRemaining,
              pricePerProfessional,
            );
          professionalsPrice = fullPricePerProfessional * professionalsToAdd;
        }
        if (professionalsPrice > 0) {
          items.push({
            label: `Profesionales adicionales (${professionalsToAdd})`,
            amount: professionalsPrice,
          });
        }
      }
    }

    // Calculate total from items to ensure consistency
    const calculatedTotal = items.reduce((sum, item) => sum + item.amount, 0);
    return {
      items,
      total: calculatedTotal,
    };
  }

  // Get breakdown for next membership period
  getNextMembershipBreakdown(): PriceBreakdown {
    const isAnnual = this.billingCycle() === 'yearly';
    const items: PriceBreakdownItem[] = [];
    const latestMembership = this.latestMembership();

    // 1. Add base plan
    const basePrice = this.basePlanPrice();
    if (basePrice > 0) {
      items.push({
        label: `Plan base (${this.plan().name})`,
        amount: basePrice,
      });
    }

    // 2. Add existing features from current membership
    if (latestMembership?.additionalFeatures) {
      latestMembership.additionalFeatures.forEach((existingFeature) => {
        // Find the feature definition to get its price
        const featureDef = this.additionalFeatures().find(
          (f) =>
            f.membershipPlanFeatureId ===
            existingFeature.membershipPlanFeatureId,
        );

        if (featureDef) {
          // Skip "Múltiples profesionales" - it's handled separately with professionals
          if (featureDef.featureName === 'Múltiples profesionales') {
            return;
          }

          const monthlyPrice = featureDef.priceMonthly ?? 0;
          if (monthlyPrice > 0) {
            const fullPrice = isAnnual
              ? monthlyPrice * 12 * 0.85
              : monthlyPrice;
            items.push({
              label: featureDef.featureName,
              amount: fullPrice,
            });
          }
        }
      });
    }

    // 3. Add existing professionals (from current membership)
    const breakdownCurrentTotalProfessionals =
      latestMembership?.numberOfProfessionals ?? 0;
    if (this.additionalProfessionalsOf(breakdownCurrentTotalProfessionals) > 0) {
      const existingAdditionalProfessionals =
        this.additionalProfessionalsOf(breakdownCurrentTotalProfessionals);
      const pricePerProfessional =
        this.currentPlan()?.pricePerProfessional ?? 0;

      if (existingAdditionalProfessionals > 0 && pricePerProfessional > 0) {
        const professionalsPrice = isAnnual
          ? pricePerProfessional * existingAdditionalProfessionals * 12 * 0.85
          : pricePerProfessional * existingAdditionalProfessionals;
        if (professionalsPrice > 0) {
          items.push({
            label: `Profesionales adicionales (${existingAdditionalProfessionals})`,
            amount: professionalsPrice,
          });
        }
      }
    }

    // 4. Add new features (only the new ones being added)
    const newFeatures = this.getSelectedNewFeatures();
    newFeatures.forEach((feature) => {
      if (feature.featureName === 'Múltiples profesionales') {
        return; // Handled separately
      }

      const monthlyPrice = feature.priceMonthly ?? 0;
      if (monthlyPrice > 0) {
        const fullPrice = isAnnual ? monthlyPrice * 12 * 0.85 : monthlyPrice;
        items.push({
          label: feature.featureName,
          amount: fullPrice,
        });
      }
    });

    // 5. Handle new additional professionals (only the increase)
    // Reuse breakdownCurrentTotalProfessionals already declared above
    const currentAdditionalProfessionals =
      this.additionalProfessionalsOf(breakdownCurrentTotalProfessionals);
    const newAdditionalProfessionals = this.numberOfProfessionals(); // Already represents only additional professionals

    // Only calculate price for the NEW additional professionals (the increase)
    const professionalsToAdd = Math.max(
      0,
      newAdditionalProfessionals - currentAdditionalProfessionals,
    );
    const pricePerProfessional = this.currentPlan()?.pricePerProfessional ?? 0;

    if (professionalsToAdd > 0 && pricePerProfessional > 0) {
      const professionalsPrice = isAnnual
        ? pricePerProfessional * professionalsToAdd * 12 * 0.85
        : pricePerProfessional * professionalsToAdd;
      if (professionalsPrice > 0) {
        items.push({
          label: `Profesionales adicionales (${professionalsToAdd})`,
          amount: professionalsPrice,
        });
      }
    }

    // Calculate total from items to ensure consistency
    const calculatedTotal = items.reduce((sum, item) => sum + item.amount, 0);
    return {
      items,
      total: calculatedTotal,
    };
  }

  // Helpers UI
  badgeClass(status: InvoiceStatus) {
    switch (status) {
      case 'paid':
        return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20 dark:bg-emerald-900/30 dark:text-emerald-300';
      case 'pending':
        return 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-300';
      case 'failed':
        return 'bg-rose-50 text-rose-700 ring-1 ring-rose-600/20 dark:bg-rose-900/30 dark:text-rose-300';
      case 'refunded':
        return 'bg-sky-50 text-sky-700 ring-1 ring-sky-600/20 dark:bg-sky-900/30 dark:text-sky-300';
    }
  }
  statusLabel(s: InvoiceStatus) {
    return s === 'paid'
      ? 'Pagado'
      : s === 'pending'
        ? 'Pendiente'
        : s === 'failed'
          ? 'Fallido'
          : 'Reembolsado';
  }

  // CONFIRMAR: Crear preferencia y REDIRIGIR al init_point
  async confirmPay() {
    // Block payment if impersonating
    if (this.authService.isImpersonating()) {
      this.paymentError.set(
        'No se puede realizar el pago de membresía mientras se está impersonando un negocio',
      );
      return;
    }

    // Check if we should show the payment options modal
    const daysRemaining = this.daysRemainingInPeriod();
    const hasUserChanges = this.hasNewAdditionalFeatures();
    const latestMembership = this.latestMembership();
    const membershipStatus =
      latestMembership?.membershipStatus?.name?.toLowerCase();

    console.log(
      '[Membership] confirmPay - daysRemaining:',
      daysRemaining,
      'hasUserChanges:',
      hasUserChanges,
      'membershipStatus:',
      membershipStatus,
    );
    console.log(
      '[Membership] selectedFeatures:',
      Array.from(this.selectedFeatures().entries()),
    );
    console.log('[Membership] latestMembership:', latestMembership);

    // Show popup ONLY if:
    // 1. There's an ACTIVE membership (not trial, not expired)
    // 2. AND there are days remaining in the current period
    // 3. AND the user changed selections (new features or more professionals)
    //
    // This means:
    // - First payment after trial: NO modal (goes directly to payment)
    // - Adding features to active membership mid-cycle: YES modal (to choose current-month or current-month-plus-next)
    const isActiveMembership = membershipStatus === 'active';
    const shouldShowModal =
      isActiveMembership && daysRemaining > 0 && hasUserChanges;

    if (shouldShowModal) {
      // Reset payment option and show modal to let user choose payment option
      this.selectedPaymentOption.set(null);
      console.log('[Membership] Mostrando popup de opciones de pago');
      this.showPaymentOptionsModal.set(true);
      return;
    }

    console.log(
      '[Membership] No se muestra popup - condiciones no cumplidas. isActiveMembership:',
      isActiveMembership,
      'daysRemaining:',
      daysRemaining,
      'hasUserChanges:',
      hasUserChanges,
    );

    // Determine payment type for backend
    // If there's an active membership and no user changes, this is a simple renewal for next period
    if (isActiveMembership && !hasUserChanges) {
      console.log(
        '[Membership] Renovación simple detectada - estableciendo opción para siguiente periodo',
      );
      this.selectedPaymentOption.set('next-period');
    } else {
      // Fallback for other scenarios (first payment after trial, etc.)
      this.selectedPaymentOption.set(null);
    }

    // Proceed with normal payment flow
    await this.processPayment(this.displayPrice());
  }

  // Process payment after user selects an option from the modal
  async proceedWithPayment(option: PaymentOption) {
    this.selectedPaymentOption.set(option);

    let amount = 0;
    if (option === 'current-month') {
      // Only pay full price for current period
      // Use breakdown total if available, otherwise fallback to calculated price
      const breakdown = this.getCurrentMonthBreakdown();
      amount =
        breakdown.total > 0
          ? breakdown.total
          : this.currentMonthAdditionalPrice();
    } else if (option === 'current-month-plus-next') {
      // Pay current period full price + next period full price
      // Use breakdown totals if available, otherwise fallback to calculated price
      const currentBreakdown = this.getCurrentMonthBreakdown();
      const nextBreakdown = this.getNextMembershipBreakdown();
      const totalFromBreakdowns = currentBreakdown.total + nextBreakdown.total;
      amount =
        totalFromBreakdowns > 0
          ? totalFromBreakdowns
          : this.currentMonthPlusNextPrice();
    } else {
      // Fallback to normal flow
      amount = this.displayPrice();
    }

    if (amount <= 0) {
      this.paymentError.set('No hay cargos adicionales que procesar.');
      this.isProcessingPayment.set(false);
      return;
    }

    await this.processPayment(amount);
  }

  // Handle payment modal cancellation
  onPaymentModalCancel() {
    this.showPaymentOptionsModal.set(false);
    this.selectedPaymentOption.set(null);
  }

  // Internal method to process the payment with ePayco
  private async processPayment(amount: number) {
    this.isProcessingPayment.set(true);
    this.paymentError.set(null);
    try {
      const { email, given_name, family_name, phone } =
        this.authService.getUserInfo() as any;

      const billingCycle = this.billingCycle();
      const responseUrl = `${window.location.origin}/settings/billing`;
      const webhookUrl = environment.epaycoWebhookUrl;
      const paymentOption = this.selectedPaymentOption() ?? '';
      const isMidCyclePurchase = paymentOption === 'current-month';

      if (this.businessId) {
        const freshMembership = await firstValueFrom(
          this.membershipSvc.getLatestBusinessMembershipFresh(this.businessId),
        );
        if (freshMembership) {
          this.latestMembership.set(freshMembership);
        }
      }

      const ePayco = await this.epaycoLoader.load();
      const handler = ePayco.checkout.configure({
        key: environment.epaycoPublicKey,
        test: environment.epaycoTestMode,
      });
      const fullName = `${given_name} ${family_name}`.trim();
      const invoice = `SUB-${this.plan().code}-${Date.now()}`;

      // Build features array for extra4
      const featuresToSend: Array<{
        membershipPlanFeatureId: string;
        quantity: number;
        featureName: string;
      }> = [];

      // Determine which features to send based on payment option
      // For mid-cycle purchases ('current-month'), only send NEW features not already in membership
      // For renewals or first purchases, send all selected features
      const latestMembership = this.latestMembership();
      const currentFeatureIds =
        latestMembership?.additionalFeatures?.map(
          (f) => f.membershipPlanFeatureId,
        ) ?? [];
      const currentNumberOfProfessionals =
        latestMembership?.numberOfProfessionals ?? 0;
      const currentAdditionalProfessionals =
        this.additionalProfessionalsOf(currentNumberOfProfessionals);

      console.log('[Membership] isMidCyclePurchase:', isMidCyclePurchase);
      console.log('[Membership] currentFeatureIds:', currentFeatureIds);
      console.log(
        '[Membership] currentAdditionalProfessionals:',
        currentAdditionalProfessionals,
      );

      // Add selected additional features
      this.selectedFeatures().forEach((selected, featureId) => {
        if (selected) {
          const feature = this.additionalFeatures().find(
            (f) => f.membershipPlanFeatureId === featureId,
          );
          if (feature) {
            // For mid-cycle purchases, only send NEW features (not already in membership)
            const isExistingFeature = currentFeatureIds.includes(featureId);

            // For "Múltiples profesionales", handle specially
            if (feature.featureName === 'Múltiples profesionales') {
              const totalAdditionalProfessionals = this.numberOfProfessionals(); // Total adicionales seleccionados

              if (isMidCyclePurchase) {
                // For mid-cycle: only send the INCREMENT (new professionals being added)
                const professionalsToAdd = Math.max(
                  0,
                  totalAdditionalProfessionals - currentAdditionalProfessionals,
                );
                console.log(
                  '[Membership] Profesionales mid-cycle: total seleccionados:',
                  totalAdditionalProfessionals,
                  ', ya pagados:',
                  currentAdditionalProfessionals,
                  ', a agregar:',
                  professionalsToAdd,
                );

                if (professionalsToAdd > 0) {
                  featuresToSend.push({
                    membershipPlanFeatureId: featureId,
                    quantity: professionalsToAdd, // Solo el incremento
                    featureName: feature.featureName,
                  });
                }
              } else {
                // For renewals: send all additional professionals
                console.log(
                  '[Membership] Profesionales renovación: enviando total adicionales:',
                  totalAdditionalProfessionals,
                );
                if (totalAdditionalProfessionals > 0) {
                  featuresToSend.push({
                    membershipPlanFeatureId: featureId,
                    quantity: totalAdditionalProfessionals,
                    featureName: feature.featureName,
                  });
                }
              }
            } else {
              // For other features
              if (isMidCyclePurchase) {
                // Only send if it's a NEW feature (not already in membership)
                if (!isExistingFeature) {
                  console.log(
                    '[Membership] Feature nueva mid-cycle:',
                    feature.featureName,
                  );
                  featuresToSend.push({
                    membershipPlanFeatureId: featureId,
                    quantity: 1,
                    featureName: feature.featureName,
                  });
                } else {
                  console.log(
                    '[Membership] Feature existente omitida mid-cycle:',
                    feature.featureName,
                  );
                }
              } else {
                // For renewals: send all selected features
                featuresToSend.push({
                  membershipPlanFeatureId: featureId,
                  quantity: 1,
                  featureName: feature.featureName,
                });
              }
            }
          }
        }
      });

      // Convert features to JSON string for extra4
      const featuresJson =
        featuresToSend.length > 0 ? JSON.stringify(featuresToSend) : '';
      console.log('[Membership] Features a enviar a Epayco:', featuresToSend);
      console.log('[Membership] JSON de features (extra4):', featuresJson);
      console.log(
        '[Membership] numberOfProfessionals actual:',
        this.numberOfProfessionals(),
      );
      console.log('[Membership] Payment amount:', amount);
      console.log('[Membership] Payment option:', this.selectedPaymentOption());
      console.log('[Membership] Response URL:', responseUrl);

      handler.open({
        // ===== Datos básicos del checkout =====
        name: this.plan().name,
        description: this.plan().name,
        currency: 'COP',
        amount, // Use the calculated amount (always full period price)
        country: 'CO',
        response: responseUrl,
        confirmation: webhookUrl,
        test: environment.epaycoTestMode,
        // ===== Suscripción =====
        planCode: this.plan().code,
        planName: this.plan().name,
        billingCycle, // 'month', 'year', etc.
        invoice,
        // ===== Metadatos que te regresan (útiles en webhook/response) =====
        extra1: this.businessId,
        extra2: fullName,
        extra3: email,
        extra4: featuresJson, // Features adicionales compradas (JSON)
        extra5: paymentOption, // Opción de pago enviada al backend
        // ===== Datos del pagador / facturación =====
        payerFirstName: given_name,
        payerLastName: family_name,
        payerEmail: email,
        payerPhone: phone,
        clientIp: this.clientIp,
        name_billing: fullName,
        email_billing: email,
        mobilephone_billing: phone ?? undefined,
        testMode: environment.epaycoTestMode,
      });
    } catch (e) {
      console.error(e);
      const apiError = this.apiErrorService.normalize(e);
      this.paymentError.set(apiError.errors[0]);
    } finally {
      this.isProcessingPayment.set(false);
    }
  }

  private loadPlansOnly() {
    this.isLoadingData.set(true);
    this.membershipSvc.getPlans().subscribe({
      next: (res) => {
        const active = (res.membershipPlans || []).filter((p) => p.isActive);
        this.allPlans.set(active);
        // Select the recommended (middle) plan by default
        const sorted = [...active].sort((a, b) => a.priceMonthly - b.priceMonthly);
        const midIndex = Math.floor(sorted.length / 2);
        const pick = sorted[midIndex] ?? active[0] ?? res.membershipPlans?.[0];
        if (pick) {
          this.applyPlanFromDto(pick);
          this.selectedPlanId.set(pick.membershipPlanId);
        }
        this.isLoadingData.set(false);
      },
      error: () => {
        // fallback mínimo si falla el API
        this.plan.set({
          code: 'pro',
          name: 'Profesional',
          priceMonthly: 99000,
          priceYearly: 1009800,
          features: [],
        });
        this.isLoadingData.set(false);
      },
    });
  }

  private applyData(
    plansRes: GetMembershipPlansResponse,
    latestRes: GetLatestBusinessMembershipResponse,
  ) {
    const activePlans = (plansRes.membershipPlans || []).filter((p) => p.isActive);
    const bm = latestRes.businessMembership ?? null;

    // Store all plans for the plan selector
    this.allPlans.set(activePlans);

    // Guarda la última membresía comprada (base para renovación)
    this.latestMembership.set(bm);

    // 1) Selecciona el plan de esa membresía; si no está en el catálogo, usa su BasicDto
    let selectedPlan: MembershipPlanDto | null = null;
    if (bm?.membershipPlanId) {
      selectedPlan =
        activePlans.find((p) => p.membershipPlanId === bm.membershipPlanId) ??
        null;
    }
    if (selectedPlan) {
      this.applyPlanFromDto(selectedPlan);
      this.selectedPlanId.set(selectedPlan.membershipPlanId);
    } else if (bm?.membershipPlan) {
      this.applyPlanFromBasicDto(bm.membershipPlan);
      this.selectedPlanId.set(bm.membershipPlanId);
    } else if (activePlans[0]) {
      // fallback: select the recommended (middle) plan, or the first one
      const sorted = [...activePlans].sort((a, b) => a.priceMonthly - b.priceMonthly);
      const midIndex = Math.floor(sorted.length / 2);
      const recommendedPlan = sorted[midIndex] ?? activePlans[0];
      this.applyPlanFromDto(recommendedPlan);
      this.selectedPlanId.set(recommendedPlan.membershipPlanId);
    }

    // 2) Inicializar features seleccionadas y número de profesionales desde la membresía existente
    const initialSelectedFeatures = new Map<string, boolean>();
    if (bm?.additionalFeatures && bm.additionalFeatures.length > 0) {
      bm.additionalFeatures.forEach((af) => {
        initialSelectedFeatures.set(af.membershipPlanFeatureId, true);
      });
    }
    this.selectedFeatures.set(initialSelectedFeatures);

    // Inicializar número de profesionales adicionales desde la membresía
    // PERO solo si el usuario no ha modificado manualmente la selección
    if (!this.hasUserModifiedSelections()) {
      if (bm?.numberOfProfessionals && bm.numberOfProfessionals > 0) {
        // numberOfProfessionals is the total; the additional ones are those above what the plan includes
        // (Básico 1, Profesional 3, Premium 5), not above 1
        const includedInPlan =
          this.currentPlan()?.numberOfProfessionals ??
          bm.membershipPlan?.numberOfProfessionals ??
          1;
        const additionalProfessionals = Math.max(
          0,
          bm.numberOfProfessionals - includedInPlan,
        );
        console.log(
          '[Membership] Inicializando profesionales adicionales desde membresía:',
          {
            total: bm.numberOfProfessionals,
            adicionales: additionalProfessionals,
          },
        );
        this.numberOfProfessionals.set(additionalProfessionals);

        // Professionals bought from the Professionals page don't add the "Múltiples profesionales"
        // feature; select it anyway so a renewal keeps every seat in use instead of dropping them
        if (additionalProfessionals > 0) {
          const multiPro = this.additionalFeatures().find(
            (f) => f.featureName === 'Múltiples profesionales',
          );
          if (multiPro && !initialSelectedFeatures.get(multiPro.membershipPlanFeatureId)) {
            initialSelectedFeatures.set(multiPro.membershipPlanFeatureId, true);
            this.selectedFeatures.set(new Map(initialSelectedFeatures));
          }
        }
      } else {
        // Si no hay número de profesionales en la membresía, usar 0 por defecto (solo el incluido)
        this.numberOfProfessionals.set(0);
      }
    } else {
      console.log(
        '[Membership] Usuario ya modificó selecciones, no inicializar desde membresía',
      );
    }

    // Resetear el flag de modificaciones del usuario al cargar desde la membresía existente
    this.hasUserModifiedSelections.set(false);

    // 3) Ciclo de facturación desde la última compra (lo que se renovaría)
    if (bm?.billingCycle?.name || bm?.billingCycle?.displayName) {
      const name = (
        bm.billingCycle.name ||
        bm.billingCycle.displayName ||
        ''
      ).toLowerCase();
      if (name.includes('month') || name.includes('mensual'))
        this.billingCycle.set('monthly');
      else if (name.includes('year') || name.includes('anual'))
        this.billingCycle.set('yearly');
    }
  }

  private loadPlansAndLatestMembership(businessId: string) {
    this.isLoadingData.set(true);
    forkJoin({
      plans: this.membershipSvc.getPlans(),
      latest: this.membershipSvc.getLatestBusinessMembership(businessId),
    }).subscribe({
      next: ({ plans, latest }) => {
        this.applyData(plans, latest);
        this.isLoadingData.set(false);
        this.membershipLoaded.set(true);
        this.membershipStatusSvc
          .getPaidProfessionalsCount()
          .subscribe((n) => this.seatCount.set(n));
      },
      error: () => {
        this.loadPlansOnly();
        this.isLoadingData.set(false);
        this.membershipLoaded.set(true);
      },
    });
  }

  private applyPlanFromDto(p: MembershipPlanDto) {
    // Store the full plan DTO
    this.currentPlan.set(p);

    const featuresIncluidas = (p.features || [])
      .filter((f) => f.isIncluded)
      .map((f) => f.featureName);
    this.plan.set({
      code: this.mapPlanCode(p.name),
      name: p.name,
      priceMonthly: p.priceMonthly,
      priceYearly: p.priceAnnual,
      features: featuresIncluidas,
      trialDays: p.trialDays ?? 0, // ⬅️ toma el trial real del plan
    });
  }

  private applyPlanFromBasicDto(p: MembershipPlanBasicDto) {
    // BasicDto doesn't have features, but we can create a partial plan for pricePerProfessional
    // Try to find the full plan from the loaded plans first
    const fullPlan = this.currentPlan();
    if (!fullPlan || fullPlan.membershipPlanId !== p.membershipPlanId) {
      // Create a minimal plan DTO with the available info
      const minimalPlan: MembershipPlanDto = {
        membershipPlanId: p.membershipPlanId,
        name: p.name,
        description: p.description ?? null,
        priceMonthly: p.priceMonthly,
        priceAnnual: p.priceAnnual,
        annualDiscountPercentage: p.annualDiscountPercentage,
        trialDays: p.trialDays,
        isActive: p.isActive,
        externalPlanId: p.externalPlanId ?? null,
        pricePerProfessional: p.pricePerProfessional ?? null,
        numberOfProfessionals: p.numberOfProfessionals ?? 1,
        createdAt: '',
        updatedAt: null,
        features: [],
      };
      this.currentPlan.set(minimalPlan);
    }

    this.plan.set({
      code: this.mapPlanCode(p.name),
      name: p.name,
      priceMonthly: p.priceMonthly,
      priceYearly: p.priceAnnual,
      features: [], // BasicDto no trae features: dejamos vacío o podríamos mostrar un placeholder
    });
  }

  // Feature selection methods
  toggleFeature(feature: MembershipPlanFeatureDto): void {
    const current = this.selectedFeatures();
    const newMap = new Map(current);
    const isSelected = newMap.get(feature.membershipPlanFeatureId) ?? false;
    newMap.set(feature.membershipPlanFeatureId, !isSelected);

    // If unchecking "Múltiples profesionales", reset quantity to 0 (solo el incluido)
    if (feature.featureName === 'Múltiples profesionales' && isSelected) {
      this.numberOfProfessionals.set(0);
    }

    // Dependency: "Ventas y reportes integrados" requires "Gestión de productos e inventario"
    if (feature.featureName === 'Ventas y reportes integrados') {
      // Find the "Gestión de productos e inventario" feature
      const productosFeature = this.additionalFeatures().find(
        (f) => f.featureName === 'Gestión de productos e inventario',
      );

      if (productosFeature) {
        if (!isSelected) {
          // Selecting "Ventas" - automatically select "Productos"
          newMap.set(productosFeature.membershipPlanFeatureId, true);
        }
        // Note: When deselecting "Ventas", we don't deselect "Productos"
        // because "Productos" can exist independently
      }
    }

    // If deselecting "Gestión de productos e inventario", also deselect "Ventas y reportes integrados"
    // because "Ventas" requires "Productos"
    if (
      feature.featureName === 'Gestión de productos e inventario' &&
      isSelected
    ) {
      // Find the "Ventas y reportes integrados" feature
      const ventasFeature = this.additionalFeatures().find(
        (f) => f.featureName === 'Ventas y reportes integrados',
      );

      if (ventasFeature) {
        // If "Ventas" is selected, deselect it when "Productos" is deselected
        // because "Ventas" cannot exist without "Productos"
        const ventasIsSelected =
          newMap.get(ventasFeature.membershipPlanFeatureId) ?? false;
        if (ventasIsSelected) {
          newMap.set(ventasFeature.membershipPlanFeatureId, false);
        }
      }
    }

    this.selectedFeatures.set(newMap);

    this.hasUserModifiedSelections.set(true);
  }

  isFeatureSelected(featureId: string): boolean {
    return this.selectedFeatures().get(featureId) ?? false;
  }

  updateProfessionals(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const value = parseInt(rawValue, 10);

    // Validar que el valor sea un número válido (0 o más, ya que 0 = solo el incluido)
    if (isNaN(value) || value < 0) {
      console.warn(
        '[Membership] Valor inválido en input de profesionales:',
        rawValue,
      );
      return;
    }

    const newValue = Math.max(0, value); // Mínimo 0 (solo el profesional incluido)
    const oldValue = this.numberOfProfessionals();

    console.log('[Membership] Actualizando profesionales adicionales:', {
      oldValue,
      newValue,
      inputValue: rawValue,
      parsedValue: value,
    });

    this.numberOfProfessionals.set(newValue);
    this.hasUserModifiedSelections.set(true);

    // Verificar que el valor se actualizó correctamente
    console.log(
      '[Membership] numberOfProfessionals (adicionales) después de actualizar:',
      this.numberOfProfessionals(),
    );
  }

  getFeaturePrice(feature: MembershipPlanFeatureDto): number {
    // Special handling for "Múltiples profesionales" - use price_per_professional from plan
    if (feature.featureName === 'Múltiples profesionales') {
      const pricePerProfessional =
        this.currentPlan()?.pricePerProfessional ?? 0;
      if (this.billingCycle() === 'monthly') {
        return pricePerProfessional;
      } else {
        // Annual: (monthly * 12) * 0.85
        return pricePerProfessional * 12 * 0.85;
      }
    }

    // For other features, use their priceMonthly
    if (this.billingCycle() === 'monthly') {
      return feature.priceMonthly ?? 0;
    } else {
      // Annual: (monthly * 12) * 0.85
      const monthlyPrice = feature.priceMonthly ?? 0;
      return monthlyPrice * 12 * 0.85;
    }
  }

  getProfessionalsCost(): number {
    return this.professionalsCost();
  }

  /**
   * Additional professionals in a membership of `total` professionals: those above what its plan
   * includes (Básico 1, Profesional 3, Premium 5). numberOfProfessionals is always the total.
   */
  private additionalProfessionalsOf(total: number): number {
    const includedInPlan =
      this.latestMembership()?.membershipPlan?.numberOfProfessionals ??
      this.currentPlan()?.numberOfProfessionals ??
      1;
    return Math.max(0, total - includedInPlan);
  }

  getTotalProfessionals(): number {
    const included = this.currentPlan()?.numberOfProfessionals ?? 1;
    return included + this.numberOfProfessionals();
  }

  updateProfessionalsFromTotal(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const value = parseInt(rawValue, 10);
    const included = this.currentPlan()?.numberOfProfessionals ?? 1;

    if (isNaN(value) || value < included) {
      input.value = String(included);
      this.numberOfProfessionals.set(0);
      this.hasUserModifiedSelections.set(true);
      return;
    }

    const additional = value - included;
    this.numberOfProfessionals.set(additional);
    this.hasUserModifiedSelections.set(true);
  }

  incrementProfessionals(): void {
    this.numberOfProfessionals.update(v => v + 1);
    this.hasUserModifiedSelections.set(true);
  }

  decrementProfessionals(): void {
    if (this.numberOfProfessionals() > 0) {
      this.numberOfProfessionals.update(v => v - 1);
      this.hasUserModifiedSelections.set(true);
    }
  }

  togglePaymentHistory(): void {
    this.isPaymentHistoryExpanded.update(v => !v);
  }

  // Plan selection for multi-plan support
  selectPlan(plan: MembershipPlanDto): void {
    this.selectedPlanId.set(plan.membershipPlanId);
    this.applyPlanFromDto(plan);

    // Reset feature selections when changing plans
    const initialSelectedFeatures = new Map<string, boolean>();
    this.selectedFeatures.set(initialSelectedFeatures);
    this.numberOfProfessionals.set(0);
    this.hasUserModifiedSelections.set(true);
  }

  isCurrentMembershipPlan(planId: string): boolean {
    const lm = this.latestMembership();
    return lm?.membershipPlanId === planId;
  }

  isSelectedPlan(planId: string): boolean {
    return this.selectedPlanId() === planId;
  }

  isRecommendedPlan(plan: MembershipPlanDto): boolean {
    // Recommend the middle-tier plan (Professional)
    const plans = this.allPlans();
    if (plans.length <= 1) return false;
    const sorted = [...plans].sort((a, b) => a.priceMonthly - b.priceMonthly);
    const midIndex = Math.floor(sorted.length / 2);
    return sorted[midIndex]?.membershipPlanId === plan.membershipPlanId;
  }

  getPlanIncludedFeaturesCount(plan: MembershipPlanDto): number {
    return (plan.features || []).filter(f => f.isIncluded).length;
  }

  getPlanIncludedFeatures(plan: MembershipPlanDto): MembershipPlanFeatureDto[] {
    return (plan.features || [])
      .filter(f => f.isIncluded && f.featureName !== 'Múltiples profesionales')
      .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
  }

  getPlanAdditionalFeatures(plan: MembershipPlanDto): MembershipPlanFeatureDto[] {
    return (plan.features || [])
      .filter(f => !f.isIncluded && f.featureName !== 'Múltiples profesionales')
      .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
  }

  /** Plans from cheapest to most expensive, so each card can build on the previous one */
  sortedPlans = computed(() =>
    [...this.allPlans()].sort((a, b) => a.priceMonthly - b.priceMonthly),
  );

  selectedPlanDto = computed(
    () => this.allPlans().find((p) => p.membershipPlanId === this.selectedPlanId()) ?? null,
  );

  /**
   * What a plan adds over the next cheaper one: "Todo lo de Básico, y además: …".
   * The cheapest plan lists everything it includes.
   */
  planFeatureDelta(plan: MembershipPlanDto): { baseName: string | null; features: MembershipPlanFeatureDto[] } {
    const plans = this.sortedPlans();
    const index = plans.findIndex((p) => p.membershipPlanId === plan.membershipPlanId);
    const included = this.getPlanIncludedFeatures(plan);
    if (index <= 0) return { baseName: null, features: included };

    const previous = plans[index - 1];
    const previousNames = new Set(this.getPlanIncludedFeatures(previous).map((f) => f.featureName));
    return {
      baseName: previous.name,
      features: included.filter((f) => !previousNames.has(f.featureName)),
    };
  }

  getPlanPrice(plan: MembershipPlanDto): number {
    return this.billingCycle() === 'monthly'
      ? plan.priceMonthly
      : plan.priceMonthly * 12 * 0.85;
  }

  private mapPlanCode(name: string): Plan['code'] {
    const n = (name || '').toLowerCase();
    if (n.includes('business') || n.includes('enterprise')) return 'business';
    if (n.includes('premium')) return 'business';
    if (n.includes('pro')) return 'pro';
    return 'starter';
  }

  // Enough to cover the visible membership periods in the unified history
  private loadPaymentHistory(page = 1, pageSize = 50) {
    this.isPaymentHistoryLoading.set(true);
    this.paymentHistoryError.set(null);
    this.membershipSvc.getPaymentHistory(page, pageSize).subscribe({
      next: (res) => {
        const mapped = res.payments.map((item) =>
          this.mapPaymentHistoryItem(item),
        );
        this.invoices.set(mapped);
        this.isPaymentHistoryLoading.set(false);
      },
      error: () => {
        this.paymentHistoryError.set(
          'No pudimos cargar el historial de pagos. Intenta nuevamente más tarde.',
        );
        this.isPaymentHistoryLoading.set(false);
      },
    });
  }

  private mapPaymentHistoryItem(item: PaymentHistoryItemDto): Invoice {
    return {
      id: item.paymentHistoryId,
      createdAt: item.paymentDate,
      amount: item.amount,
      currency: item.currency,
      status: this.mapStatusToInvoice(item.status),
      receiptUrl: item.receiptUrl ?? undefined,
      description: item.description || item.planName || 'Pago de suscripción',
      period: {
        start: item.billingPeriodStart,
        end: item.billingPeriodEnd,
      },
      failureReason: item.failureReason,
      externalPaymentId: item.externalPaymentId,
      externalTransactionId: item.externalTransactionId,
      planName: item.planName,
      features: item.features?.map((f) => ({
        featureName: f.featureName,
        pricePaid: f.pricePaid,
        quantity: f.quantity,
      })),
    };
  }

  private mapStatusToInvoice(rawStatus: string): InvoiceStatus {
    const status = (rawStatus || '').toLowerCase();
    if (
      ['paid', 'success', 'approved', 'aceptada', 'aprobada'].includes(status)
    ) {
      return 'paid';
    }
    if (
      [
        'failed',
        'rechazada',
        'rejected',
        'error',
        'cancelled',
        'cancelada',
      ].includes(status)
    ) {
      return 'failed';
    }
    if (['refunded', 'refund', 'reembolsado', 'reembolsada'].includes(status)) {
      return 'refunded';
    }
    return 'pending';
  }

  private handlePaymentCallback() {
    const params = this.route.snapshot.queryParamMap;
    const knownKeys = [
      'ref_payco',
      'x_ref_payco',
      'x_transaction_id',
      'x_amount',
      'x_currency_code',
      'x_response',
      'x_response_reason_text',
      'x_test',
    ];
    const hasPaymentParams = knownKeys.some((key) => params.get(key));
    if (!hasPaymentParams) return;

    const result: PaymentCallbackResult = {
      refPayco:
        params.get('ref_payco') ?? params.get('x_ref_payco') ?? undefined,
      transactionId:
        params.get('x_transaction_id') ??
        params.get('transactionId') ??
        undefined,
      status: params.get('x_response') ?? params.get('status') ?? undefined,
      message:
        params.get('x_response_reason_text') ??
        params.get('message') ??
        undefined,
      amount: this.parseAmount(params.get('x_amount') ?? params.get('amount')),
      currency:
        params.get('x_currency_code') ?? params.get('currency') ?? 'COP',
      isTest: (params.get('x_test') ?? '').toLowerCase() === 'true',
    };

    this.paymentResult.set(result);

    // ePayco usually comes back with only ref_payco (no status), so anything but a
    // rejection may have paid: watch the membership until the webhook applies it
    const variant = this.resolvePaymentVariant(result);
    if (variant !== 'error') {
      this.membershipStatusSvc.syncAfterPayment(result.refPayco);
    }
    this.loadPaymentHistory();

    // Remove params from URL
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  private parseAmount(value: string | null): number | undefined {
    if (!value) return undefined;
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : undefined;
  }

  clearPaymentResult() {
    this.paymentResult.set(null);
  }

  /** Reload the plan and payments once a payment changed the membership */
  private reloadAfterPayment() {
    if (this.businessId) {
      this.loadPlansAndLatestMembership(this.businessId);
    }
    this.loadPaymentHistory();
  }

  membershipStatusClass(status: string) {
    switch ((status || '').toLowerCase()) {
      case 'active':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300';
      case 'trial':
        return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300';
      case 'pending':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300';
      case 'cancelled':
      case 'suspended':
        return 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
    }
  }

  private resolvePaymentVariant(
    result: PaymentCallbackResult | null,
  ): PaymentResultVariant {
    if (!result?.status) return 'info';
    const status = result.status.toLowerCase();
    if (
      ['aceptada', 'aprobada', 'approved', 'paid', 'success', 'ok'].includes(
        status,
      )
    ) {
      return 'success';
    }
    if (['pendiente', 'pending', 'processing'].includes(status)) {
      return 'warning';
    }
    if (
      [
        'rechazada',
        'rejected',
        'failed',
        'error',
        'cancelada',
        'cancelado',
      ].includes(status)
    ) {
      return 'error';
    }
    return 'info';
  }

  private resolvePaymentTitle(variant: PaymentResultVariant): string {
    switch (variant) {
      case 'success':
        return 'Pago aprobado';
      case 'warning':
        return 'Pago en proceso';
      case 'error':
        return 'Pago rechazado';
      default:
        return 'Resultado del pago';
    }
  }

  async downloadReceipt(inv: Invoice) {
    if (typeof window === 'undefined') return;
    const doc = new jsPDF();

    // Header: Logo left, Title right (matching sales receipt design)
    let headerY = 20;
    try {
      const logo = await this.getLogoDataUrl();
      if (logo) {
        doc.addImage(logo, 'PNG', 20, 12, 45, 7);
      } else {
        const businessInfo = this.authService.getUserInfo();
        const businessName = businessInfo?.business_name || 'CitaLink Pro';
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.text(businessName, 20, headerY);
      }
    } catch {
      const businessInfo = this.authService.getUserInfo();
      const businessName = businessInfo?.business_name || 'CitaLink Pro';
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.text(businessName, 20, headerY);
    }

    // Title right aligned (size 16 bold)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('COMPROBANTE DE VENTA', 190, headerY, { align: 'right' });

    // Date right aligned below title (PaddingTop 10 equivalent = ~7mm)
    // Format: dd/MM/yyyy HH:mm (matching sales receipt)
    const paymentDate = new Date(inv.createdAt);
    const dateStr = paymentDate.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const timeStr = paymentDate.toLocaleTimeString('es-CO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Fecha: ${dateStr} ${timeStr}`, 190, headerY + 7, {
      align: 'right',
    });

    // Content starts with PaddingVertical(10) = ~7mm from header
    let y = 47; // ~20 (header) + 7 (padding) + 20 (content start)

    // Business name (size 14 bold, PaddingBottom(10) = ~7mm)
    const businessInfo = this.authService.getUserInfo();
    if (businessInfo?.business_name) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(businessInfo.business_name, 20, y);
      y += 7; // PaddingBottom(10) equivalent
    }

    // Client section (PaddingBottom(10) = ~7mm)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Cliente:', 20, y);
    y += 5; // Small spacing before client name

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const userInfo = this.authService.getUserInfo();
    if (userInfo) {
      const clientName =
        `${userInfo.given_name || ''} ${userInfo.family_name || ''}`.trim() ||
        userInfo.email ||
        'Cliente';
      doc.text(clientName, 20, y);
      y += 5;
      if (userInfo.email) {
        doc.text(`Email: ${userInfo.email}`, 20, y);
        y += 5;
      }
      if (userInfo.phone) {
        doc.text(`Teléfono: ${userInfo.phone}`, 20, y);
        y += 5;
      }
    }
    y += 2; // Complete PaddingBottom(10) spacing

    // Items table (PaddingBottom(10) = ~7mm)
    // Table header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Descripción', 20, y);
    doc.text('Cantidad', 120, y, { align: 'right' });
    doc.text('Precio Unit.', 150, y, { align: 'right' });
    doc.text('Total', 190, y, { align: 'right' });
    y += 3;
    doc.line(20, y, 190, y);
    y += 5;

    // Determine payment type based on data, not description:
    // 1. Solo membresía: tiene planName, monto significativo, sin features o monto = precio plan
    // 2. Membresía + features: tiene planName, features, y monto > suma de features
    // 3. Solo features: tiene features, y monto = suma de features (o muy cercano)
    const description = inv.description || '';
    const isProfessionalAdditional = description.includes(
      'Profesional adicional',
    );

    // Calculate total features amount
    const featuresTotal =
      inv.features?.reduce((sum, f) => sum + f.pricePaid, 0) ?? 0;

    // Prices of the plan this payment was for (it may differ from the current plan)
    const { monthlyPrice, annualPrice } = this.planPricesFor(inv.planName);

    // Determine if payment includes membership base
    // If amount is significantly greater than features total, it includes membership
    // Or if there's no features but there's a planName and significant amount
    const hasPlanName = !!inv.planName;
    const hasFeatures = inv.features && inv.features.length > 0;
    const amountIncludesMembership =
      hasPlanName &&
      (!hasFeatures || // No features = membership only
        inv.amount - featuresTotal > 1000); // Amount significantly greater than features = membership + features
    const isFeaturesOnly =
      hasFeatures && Math.abs(inv.amount - featuresTotal) < 100; // Amount equals features (within $100 tolerance)

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    if (isProfessionalAdditional) {
      // Extract amount from description (format: "Profesional adicional - 20.000 COP")
      const amountMatch = description.match(/([\d.,]+)\s*COP/);
      const unitPrice = amountMatch
        ? parseFloat(amountMatch[1].replace(/\./g, '').replace(',', '.'))
        : inv.amount;

      doc.text('Profesional adicional', 20, y);
      doc.text('1', 120, y, { align: 'right' });
      doc.text(this.formatCurrency(unitPrice, inv.currency || 'COP'), 150, y, {
        align: 'right',
      });
      doc.text(this.formatCurrency(inv.amount, inv.currency || 'COP'), 190, y, {
        align: 'right',
      });
      y += 6; // Cell padding
    } else if (isFeaturesOnly) {
      // Case 3: Solo features adicionales (mid-cycle) - NO mostrar membresía base
      for (const feature of inv.features!) {
        const featureLabel = this.featureLabel(feature.featureName, feature.quantity);
        doc.text(featureLabel, 20, y);
        doc.text(feature.quantity.toString(), 120, y, { align: 'right' });
        const unitPrice =
          feature.quantity > 0
            ? feature.pricePaid / feature.quantity
            : feature.pricePaid;
        doc.text(
          this.formatCurrency(unitPrice, inv.currency || 'COP'),
          150,
          y,
          { align: 'right' },
        );
        doc.text(
          this.formatCurrency(feature.pricePaid, inv.currency || 'COP'),
          190,
          y,
          { align: 'right' },
        );
        y += 6; // Cell padding
      }
    } else if (amountIncludesMembership) {
      // Case 1 or 2: Membresía sola o Membresía + features
      const planName = inv.planName || this.plan().name || 'Plan de membresía';

      // Determine plan base price using the actual plan price (monthly or annual)
      // instead of calculating by subtraction which fails for multi-period payments
      let isAnnual: boolean;

      if (inv.period?.start && inv.period?.end) {
        const periodStart = new Date(inv.period.start);
        const periodEnd = new Date(inv.period.end);
        const periodDurationDays = Math.round(
          (periodEnd.getTime() - periodStart.getTime()) /
            (1000 * 60 * 60 * 24),
        );
        isAnnual = periodDurationDays > 200;
      } else {
        isAnnual = inv.amount >= annualPrice * 0.9;
      }
      let planBasePrice = isAnnual ? annualPrice : monthlyPrice;

      // Validate calculated price (should be positive and less than total amount)
      if (planBasePrice <= 0 || planBasePrice < 1000 || planBasePrice > inv.amount) {
        planBasePrice = monthlyPrice;
        isAnnual = false;
      }

      // Show plan base: the cycle comes from the period, never from the price
      const billingType = isAnnual ? 'Anual' : 'Mensual';
      doc.text(`${planName} (${billingType})`, 20, y);
      doc.text('1', 120, y, { align: 'right' });
      doc.text(
        this.formatCurrency(planBasePrice, inv.currency || 'COP'),
        150,
        y,
        { align: 'right' },
      );
      doc.text(
        this.formatCurrency(planBasePrice, inv.currency || 'COP'),
        190,
        y,
        { align: 'right' },
      );
      y += 6; // Cell padding

      // Show each additional feature/professional as a separate line
      if (hasFeatures && inv.features) {
        // Calculate the actual total paid for features (amount - plan base)
        // and adjust proportionally since stored pricePaid may not match
        // (e.g., when paying for current month + next month)
        const actualFeaturesTotal = inv.amount - planBasePrice;
        const storedFeaturesTotal = featuresTotal;
        const adjustmentFactor = storedFeaturesTotal > 0
          ? actualFeaturesTotal / storedFeaturesTotal
          : 1;

        for (const feature of inv.features) {
          const featureLabel = this.featureLabel(feature.featureName, feature.quantity);
          doc.text(featureLabel, 20, y);
          doc.text(feature.quantity.toString(), 120, y, { align: 'right' });
          const adjustedPricePaid = feature.pricePaid * adjustmentFactor;
          const unitPrice =
            feature.quantity > 0
              ? adjustedPricePaid / feature.quantity
              : adjustedPricePaid;
          doc.text(
            this.formatCurrency(unitPrice, inv.currency || 'COP'),
            150,
            y,
            { align: 'right' },
          );
          doc.text(
            this.formatCurrency(adjustedPricePaid, inv.currency || 'COP'),
            190,
            y,
            { align: 'right' },
          );
          y += 6; // Cell padding
        }
      } else {
        // No features but amount might include something else (e.g., professionals)
        // Check if amount is greater than plan base
        const additionalAmount = inv.amount - planBasePrice;
        if (additionalAmount > 100) {
          // Only show if difference is significant (> $100)
          doc.text('Funcionalidades y profesionales adicionales', 20, y);
          doc.text('1', 120, y, { align: 'right' });
          doc.text(
            this.formatCurrency(additionalAmount, inv.currency || 'COP'),
            150,
            y,
            { align: 'right' },
          );
          doc.text(
            this.formatCurrency(additionalAmount, inv.currency || 'COP'),
            190,
            y,
            { align: 'right' },
          );
          y += 6; // Cell padding
        }
      }
    } else {
      // Fallback: show description as item
      const itemName = description || inv.planName || 'Item';
      doc.text(itemName, 20, y);
      doc.text('1', 120, y, { align: 'right' });
      doc.text(this.formatCurrency(inv.amount, inv.currency || 'COP'), 150, y, {
        align: 'right',
      });
      doc.text(this.formatCurrency(inv.amount, inv.currency || 'COP'), 190, y, {
        align: 'right',
      });
      y += 6; // Cell padding
    }

    // Totals (PaddingTop(10) = ~7mm, right aligned)
    y += 4; // Complete PaddingBottom(10) from table + PaddingTop(10)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Total:', 150, y, { align: 'right' });
    doc.text(this.formatCurrency(inv.amount, inv.currency || 'COP'), 190, y, {
      align: 'right',
    });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Pagado:', 150, y, { align: 'right' });
    doc.text(this.formatCurrency(inv.amount, inv.currency || 'COP'), 190, y, {
      align: 'right',
    });

    // Payment method and reference (PaddingTop(10) = ~7mm)
    y += 3; // PaddingTop(10) equivalent
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (inv.method) {
      const methodName =
        inv.method.brand === 'visa'
          ? 'Tarjeta Visa'
          : inv.method.brand === 'mastercard'
            ? 'Tarjeta Mastercard'
            : inv.method.brand === 'amex'
              ? 'Tarjeta American Express'
              : 'Tarjeta';
      doc.text(`Método de pago: ${methodName}`, 20, y);
    } else {
      doc.text('Método de pago: ePayco', 20, y);
    }
    y += 5;
    if (inv.externalTransactionId) {
      doc.text(`Referencia: ${inv.externalTransactionId}`, 20, y);
      y += 5;
    }
    if (inv.period) {
      doc.text(
        `Periodo: ${this.formatDate(inv.period.start)} – ${this.formatDate(inv.period.end)}`,
        20,
        y,
      );
    }

    doc.save(`comprobante-${inv.id}.pdf`);
  }

  private formatCurrency(amount: number, currency = 'COP'): string {
    // Format matching sales receipt: $3,000 (with comma as thousands separator, no decimals)
    // Backend uses $amount:N0 which formats as $3,000
    return `$${Math.round(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  private formatDate(date: string | Date) {
    return new Date(date).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  private logoDataUrl: string | null = null;
  private logoPromise: Promise<string | null> | null = null;

  private async getLogoDataUrl(): Promise<string | null> {
    if (this.logoDataUrl) return this.logoDataUrl;
    if (this.logoPromise) return this.logoPromise;
    if (typeof document === 'undefined') return null;

    this.logoPromise = (async () => {
      try {
        const response = await fetch('assets/images/logo/new-logo.svg');
        const svgText = await response.text();
        const svgBase64 = window.btoa(unescape(encodeURIComponent(svgText)));
        const svgDataUrl = `data:image/svg+xml;base64,${svgBase64}`;
        const img = await this.loadImage(svgDataUrl);
        const canvas = document.createElement('canvas');
        canvas.width = 184;
        canvas.height = 32;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0);
        this.logoDataUrl = canvas.toDataURL('image/png');
        return this.logoDataUrl;
      } catch {
        return null;
      } finally {
        this.logoPromise = null;
      }
    })();

    return this.logoPromise;
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
      img.src = src;
    });
  }

  /**
   * Name of an add-on line in receipts and reports. Extra professionals read the same whether
   * they were bought from the plan customizer or from the Professionals page.
   */
  private featureLabel(featureName: string, quantity: number): string {
    if (featureName === 'Múltiples profesionales') {
      return quantity > 1 ? `Profesionales adicionales (x${quantity})` : 'Profesional adicional';
    }
    return quantity > 1 ? `${featureName} (x${quantity})` : featureName;
  }

  /** Name of a payment that has no feature details, e.g. "Profesional adicional (período actual) - 15,000 COP" */
  private standalonePaymentLabel(description?: string | null): string {
    if (!description) return 'Funcionalidad adicional';
    if (description.startsWith('Profesional adicional')) return 'Profesional adicional';
    return description;
  }

  /**
   * Prices of the plan a payment was for, looked up by name; falls back to the current plan.
   * The annual price is the plan's own, or 12 months with a 15% discount when it has none.
   */
  private planPricesFor(planName?: string | null): { monthlyPrice: number; annualPrice: number } {
    const name = (planName || '').trim().toLowerCase();
    const plan =
      (name && this.allPlans().find((p) => p.name.trim().toLowerCase() === name)) ||
      this.currentPlan();
    const monthlyPrice = plan?.priceMonthly ?? this.plan().priceMonthly;
    const annualPrice = plan?.priceAnnual && plan.priceAnnual > 0 ? plan.priceAnnual : monthlyPrice * 12 * 0.85;
    return { monthlyPrice, annualPrice };
  }

  async downloadMembershipReport() {
    if (typeof window === 'undefined') return;

    this.isDownloadingMembershipReport.set(true);

    try {
      const response = await firstValueFrom(
        this.membershipSvc.getAllPaymentHistory(),
      );
      if (!response) {
        throw new Error('No se pudo obtener el historial de pagos');
      }

      // Filtrar solo pagos exitosos
      const paidPayments = response.payments.filter((p) => p.status === 'paid');

      if (paidPayments.length === 0) {
        alert('No hay pagos exitosos para generar el reporte');
        this.isDownloadingMembershipReport.set(false);
        return;
      }

      // Ordenar por fecha de período de facturación
      paidPayments.sort((a, b) => {
        const dateA = new Date(a.billingPeriodStart).getTime();
        const dateB = new Date(b.billingPeriodStart).getTime();
        return dateA - dateB;
      });

      const doc = new jsPDF();

      // Header: Logo left, Title right (matching sales receipt design)
      let headerY = 20;
      try {
        const logo = await this.getLogoDataUrl();
        if (logo) {
          doc.addImage(logo, 'PNG', 20, 12, 45, 7);
        } else {
          const businessInfo = this.authService.getUserInfo();
          const businessName = businessInfo?.business_name || 'CitaLink Pro';
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(20);
          doc.text(businessName, 20, headerY);
        }
      } catch {
        const businessInfo = this.authService.getUserInfo();
        const businessName = businessInfo?.business_name || 'CitaLink Pro';
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.text(businessName, 20, headerY);
      }

      // Title right aligned (size 16 bold)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('REPORTE DE MEMBRESÍAS', 190, headerY, { align: 'right' });

      // Date right aligned below title
      const now = new Date();
      const dateStr = now.toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const timeStr = now.toLocaleTimeString('es-CO', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Fecha: ${dateStr} ${timeStr}`, 190, headerY + 7, {
        align: 'right',
      });

      // Content starts with PaddingVertical(10) = ~7mm from header
      let y = 47; // ~20 (header) + 7 (padding) + 20 (content start)

      // Business name (size 14 bold, PaddingBottom(10) = ~7mm)
      const businessInfo = this.authService.getUserInfo();
      if (businessInfo?.business_name) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(businessInfo.business_name, 20, y);
        y += 7; // PaddingBottom(10) equivalent
      }

      // Client section (PaddingBottom(10) = ~7mm)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Cliente:', 20, y);
      y += 5; // Small spacing before client name

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const userInfo = this.authService.getUserInfo();
      if (userInfo) {
        const clientName =
          `${userInfo.given_name || ''} ${userInfo.family_name || ''}`.trim() ||
          userInfo.email ||
          'Cliente';
        doc.text(clientName, 20, y);
        y += 5;
        if (userInfo.email) {
          doc.text(`Email: ${userInfo.email}`, 20, y);
          y += 5;
        }
        if (userInfo.phone) {
          doc.text(`Teléfono: ${userInfo.phone}`, 20, y);
          y += 5;
        }
      }
      y += 2; // Complete PaddingBottom(10) spacing

      // Table header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Período', 20, y);
      doc.text('Descripción', 60, y);
      doc.text('Cantidad', 120, y, { align: 'right' });
      doc.text('Precio Unit.', 150, y, { align: 'right' });
      doc.text('Total', 190, y, { align: 'right' });
      y += 3;
      doc.line(20, y, 190, y);
      y += 5;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);

      // Helper to create a unique period key
      const getPeriodKey = (start: Date, end: Date): string => {
        return `${start.getTime()}_${end.getTime()}`;
      };

      // Group all payments by unique billing period
      // Each unique period will be a separate section in the report
      type PaymentHistoryFeatureDto = {
        featureName: string;
        pricePaid: number;
        quantity: number;
      };

      const periodMap = new Map<
        string,
        {
          periodStart: Date;
          periodEnd: Date;
          items: Array<{
            type: 'membership' | 'feature';
            payment: (typeof paidPayments)[0];
            planBasePrice?: number;
            planName?: string;
            planType?: string;
            feature?: PaymentHistoryFeatureDto;
          }>;
        }
      >();

      // Process each payment and group by period
      for (const payment of paidPayments) {
        const paymentPeriodStart = new Date(payment.billingPeriodStart);
        const paymentPeriodEnd = new Date(payment.billingPeriodEnd);
        const periodKey = getPeriodKey(paymentPeriodStart, paymentPeriodEnd);

        // Get or create period entry
        let periodEntry = periodMap.get(periodKey);
        if (!periodEntry) {
          periodEntry = {
            periodStart: paymentPeriodStart,
            periodEnd: paymentPeriodEnd,
            items: [],
          };
          periodMap.set(periodKey, periodEntry);
        }

        const descLower = (payment.description || '').toLowerCase();
        const isFullMembership =
          descLower.includes('suscripción') ||
          descLower.includes('subscription');

        // If this is a full membership payment, add it as a membership item
        if (isFullMembership) {
          const planName =
            payment.planName || this.plan().name || 'Plan de membresía';

          // Calculate features total first
          const featuresTotal =
            payment.features?.reduce((sum, f) => sum + f.pricePaid, 0) ?? 0;

          // Determine plan base price using the actual plan price (monthly or annual)
          // based on the billing period duration, instead of subtracting features
          // which fails for multi-period payments (e.g., current month + next month)
          // Prices of the plan this payment was for, not the current plan's
          const { monthlyPrice, annualPrice } = this.planPricesFor(payment.planName);
          let isAnnual: boolean;

          if (payment.billingPeriodStart && payment.billingPeriodEnd) {
            const periodStart = new Date(payment.billingPeriodStart);
            const periodEnd = new Date(payment.billingPeriodEnd);
            const periodDurationDays = Math.round(
              (periodEnd.getTime() - periodStart.getTime()) /
                (1000 * 60 * 60 * 24),
            );
            isAnnual = periodDurationDays > 200;
          } else {
            isAnnual = payment.amount >= annualPrice * 0.9;
          }
          let planBasePrice = isAnnual ? annualPrice : monthlyPrice;

          // Validate: plan price should be positive and not exceed the total amount
          if (planBasePrice <= 0 || planBasePrice < 1000 || planBasePrice > payment.amount) {
            planBasePrice = monthlyPrice;
            isAnnual = false;
          }

          // Always add as membership since this is a subscription payment.
          // The cycle comes from the period, never from the price
          const planType = isAnnual ? 'Anual' : 'Mensual';

          periodEntry.items.push({
            type: 'membership',
            payment,
            planBasePrice,
            planName,
            planType,
          });
        }

        // Process features from this payment
        if (payment.features && payment.features.length > 0) {
          for (const feature of payment.features) {
            periodEntry.items.push({
              type: 'feature',
              payment,
              feature,
            });
          }
        }

        // If this is a standalone feature payment (no features array and not a membership)
        // OR if it's a membership but the base price was invalid and it has no features
        // In this case, we add the payment as a feature item so it's not lost
        const wasMembershipAdded =
          isFullMembership &&
          periodEntry.items.some(
            (item) =>
              item.type === 'membership' &&
              item.payment.paymentHistoryId === payment.paymentHistoryId,
          );

        if (
          !wasMembershipAdded &&
          (!payment.features || payment.features.length === 0)
        ) {
          // Payment wasn't added as membership (invalid price) and has no features
          // Add it as a feature item so it appears in the report
          periodEntry.items.push({
            type: 'feature',
            payment,
          });
        }
      }

      // After processing all payments, check each period and add membership base if missing
      for (const periodEntry of periodMap.values()) {
        // Check if this period has items but no membership row
        const hasMembershipRow = periodEntry.items.some(
          (item) => item.type === 'membership',
        );
        const hasItems = periodEntry.items.length > 0;

        if (hasItems && !hasMembershipRow) {
          // Calculate period duration in days
          const periodDurationDays = Math.round(
            (periodEntry.periodEnd.getTime() -
              periodEntry.periodStart.getTime()) /
              (1000 * 60 * 60 * 24),
          );

          // Determine if monthly or annual based on period duration
          // Monthly: ~30 days, Annual: ~365 days
          const isAnnual = periodDurationDays > 200; // More than 200 days = annual

          // Get plan name from first payment in period or use current plan
          const firstPayment = periodEntry.items[0]?.payment;
          const planName =
            firstPayment?.planName ||
            this.currentPlan()?.name ||
            this.plan().name ||
            'Plan de membresía';

          // Prices of the plan that period was for
          const { monthlyPrice, annualPrice } = this.planPricesFor(planName);
          const planBasePrice = isAnnual ? annualPrice : monthlyPrice;
          const planType = isAnnual ? 'Anual' : 'Mensual';

          // Add membership base row at the beginning of items array
          periodEntry.items.unshift({
            type: 'membership',
            payment: firstPayment,
            planBasePrice,
            planName,
            planType,
          });
        }
      }

      // Convert map to array and sort by period start date
      const periods = Array.from(periodMap.values()).sort(
        (a, b) => a.periodStart.getTime() - b.periodStart.getTime(),
      );

      // Process each period as a separate section
      for (const period of periods) {
        const periodStr = `${this.formatDateShort(period.periodStart)} – ${this.formatDateShort(period.periodEnd)}`;

        // Check if we need a new page
        if (y > 250) {
          doc.addPage();
          y = 20;
        }

        // Add visual separator before each period (except the first one)
        if (periods.indexOf(period) > 0) {
          y += 3;
          doc.line(20, y, 190, y);
          y += 5;
        }

        // Process items in this period
        let periodTotal = 0;
        const periodCurrency = period.items[0]?.payment.currency || 'COP';

        // Pre-calculate adjustment factors per payment for feature prices
        // When paying for multiple periods (e.g., current + next month),
        // stored feature pricePaid may not match the actual amount paid
        const adjustmentFactors = new Map<string, number>();
        const membershipItems = period.items.filter(i => i.type === 'membership');
        for (const membershipItem of membershipItems) {
          const paymentId = membershipItem.payment.paymentHistoryId;
          const planBase = membershipItem.planBasePrice ?? 0;
          const paymentAmount = membershipItem.payment.amount;
          const storedFeaturesTotal = period.items
            .filter(i => i.type === 'feature' && i.feature && i.payment.paymentHistoryId === paymentId)
            .reduce((sum, i) => sum + (i.feature?.pricePaid ?? 0), 0);
          if (storedFeaturesTotal > 0) {
            const actualFeaturesTotal = paymentAmount - planBase;
            adjustmentFactors.set(paymentId, actualFeaturesTotal / storedFeaturesTotal);
          }
        }

        for (const item of period.items) {
          if (y > 250) {
            doc.addPage();
            y = 20;
          }

          if (
            item.type === 'membership' &&
            item.planBasePrice &&
            item.planBasePrice > 0
          ) {
            // Membership base plan
            doc.text(periodStr, 20, y);
            doc.text(`${item.planName} (${item.planType})`, 60, y);
            doc.text('1', 120, y, { align: 'right' });
            doc.text(
              this.formatCurrency(item.planBasePrice, periodCurrency),
              150,
              y,
              { align: 'right' },
            );
            doc.text(
              this.formatCurrency(item.planBasePrice, periodCurrency),
              190,
              y,
              { align: 'right' },
            );
            periodTotal += item.planBasePrice;
            y += 6; // Cell padding
          } else if (item.type === 'feature') {
            if (item.feature) {
              // Feature with details - adjust price proportionally if needed
              const featureLabel = this.featureLabel(item.feature.featureName, item.feature.quantity);

              const factor = adjustmentFactors.get(item.payment.paymentHistoryId) ?? 1;
              const adjustedPricePaid = item.feature.pricePaid * factor;

              doc.text(periodStr, 20, y);
              doc.text(featureLabel, 60, y);
              doc.text(item.feature.quantity.toString(), 120, y, {
                align: 'right',
              });
              const unitPrice =
                item.feature.quantity > 0
                  ? adjustedPricePaid / item.feature.quantity
                  : adjustedPricePaid;
              doc.text(this.formatCurrency(unitPrice, periodCurrency), 150, y, {
                align: 'right',
              });
              doc.text(
                this.formatCurrency(adjustedPricePaid, periodCurrency),
                190,
                y,
                { align: 'right' },
              );
              periodTotal += adjustedPricePaid;
              y += 6; // Cell padding
            } else {
              // Standalone feature payment (no feature details)
              doc.text(periodStr, 20, y);
              doc.text(
                this.standalonePaymentLabel(item.payment.description),
                60,
                y,
              );
              doc.text('1', 120, y, { align: 'right' });
              doc.text(
                this.formatCurrency(item.payment.amount, periodCurrency),
                150,
                y,
                { align: 'right' },
              );
              doc.text(
                this.formatCurrency(item.payment.amount, periodCurrency),
                190,
                y,
                { align: 'right' },
              );
              periodTotal += item.payment.amount;
              y += 6; // Cell padding
            }
          }
        }

        // Total por período
        y += 2; // Small spacing before total
        if (y > 250) {
          doc.addPage();
          y = 20;
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('Total período:', 150, y, { align: 'right' });
        doc.text(this.formatCurrency(periodTotal, periodCurrency), 190, y, {
          align: 'right',
        });
        doc.setFont('helvetica', 'normal');
        y += 4; // Spacing after total

        // Add spacing between periods
        y += 3;
      }

      // Totals section
      y += 4;
      if (y > 250) {
        doc.addPage();
        y = 20;
      }

      const totalAmount = paidPayments.reduce((sum, p) => sum + p.amount, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Total General:', 150, y, { align: 'right' });
      doc.text(this.formatCurrency(totalAmount, 'COP'), 190, y, {
        align: 'right',
      });

      doc.save(`reporte-membresias-${now.toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating membership report:', error);
      alert(
        'Error al generar el reporte de membresías. Por favor, intenta nuevamente.',
      );
    } finally {
      this.isDownloadingMembershipReport.set(false);
    }
  }

  private formatDateShort(date: Date | string): string {
    return new Date(date).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}
